import { catchError, mergeMap } from "rxjs/operators";
import { throwError, empty, from, of } from "rxjs";
import { ajax } from "rxjs/ajax";

import { TENANT_DOMAIN, APP_TYPE, APP_VERSION, DEVICE_CACHE_TTL_MS, DEVICE_CACHE_SCHEMA_VERSION, getCurrentApiBaseUrl } from "./constants";
import { getCached, setCached, clearApiCache, onApiCacheClear } from "./idbCache";
import { getAccessToken, refreshAccessToken } from "./authToken";
import { LOGIN } from "./routes";

let userDetails = localStorage.getItem("user-details") && JSON.parse(localStorage.getItem("user-details")),
  loggedUserRole = userDetails !== null ? userDetails.roles[0].role : "",
  isUnauthorizedHandled = false;

const getHeaders = (appVersion, refreshToken = "") => {
  const headers = {
    "X-APP-TYPE": APP_TYPE,
    "X-TENANT-DOMAIN": TENANT_DOMAIN,
    "X-API-VERSION": appVersion || APP_VERSION,
  };

  let token = localStorage.getItem("user-details") && JSON.parse(localStorage.getItem("user-details")).token;
  if (refreshToken && loggedUserRole === "Guest") {
    token = refreshToken;
  }

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
};

const unathorized = (err) => {
  if (err.status === 401 && !isUnauthorizedHandled) {
    isUnauthorizedHandled = true;
    localStorage.removeItem("user-details");
    localStorage.removeItem("fdaView");
    clearApiCache();
    const urlParams = new URLSearchParams(window.location.search);
    const showBanner = urlParams.get('showbanner');
    const loginUrl = showBanner == '1' 
    ? `${window.location.origin}${LOGIN}?showbanner=1`
    : `${window.location.origin}${LOGIN}`;
    
    alert('Unauthorized');
    window.location.replace(loginUrl);
    return empty();
  }
  return throwError(err);
};

// Runs an authenticated request. On a 401 it refreshes the access token once
// and repeats the request with the new token; only if that fails is the user
// logged out. `makeRequest` must build the request (and its headers) each time
// it is called, so the retry carries the fresh token.
const withTokenRefresh = (makeRequest) => {
  const usedToken = getAccessToken();
  return makeRequest().pipe(
    catchError((err) => {
      if (err?.status !== 401) return throwError(err);
      return from(refreshAccessToken(usedToken)).pipe(
        mergeMap((refreshed) => (refreshed ? makeRequest() : throwError(err)))
      );
    }),
    catchError(unathorized)
  );
};

// fetch() version of withTokenRefresh: resolves with the final Response.
const fetchWithTokenRefresh = async (makeRequest) => {
  const usedToken = getAccessToken();
  const response = await makeRequest();
  if (response.status !== 401) return response;
  return (await refreshAccessToken(usedToken)) ? makeRequest() : response;
};

export const get = (url, headers = {}) => {
  return withTokenRefresh(() =>
    ajax({
      url: url,
      method: "GET",
      responseType: "text",
      headers: {
        ...getHeaders(),
        ...headers,
      },
      crossDomain: true,
    })
  ).pipe(mergeMap(({ response }) => of(response)));
};

export const remove = (url, headers = {}) => {
  return withTokenRefresh(() =>
    ajax({
      url: url,
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...getHeaders(),
        ...headers,
      },
      responseType: "json",
      crossDomain: true,
    })
  ).pipe(mergeMap(({ response: data }) => of(data.response)));
};

export const post = (url, body = {}, responseType = "text") => {
  return withTokenRefresh(() =>
    ajax({
      url: url,
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        ...getHeaders(),
      },
      body,
      responseType,
      crossDomain: true,
    })
  );
};

export const postJson = (url, body = {}, responseType = "text") => {
  return withTokenRefresh(() =>
    ajax({
      url: url,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getHeaders(),
      },
      body,
      async: true,
      responseType: responseType,
      crossDomain: true,
    })
  );
};

export const postVersionJson = (url, body = {}, responseType = "text", version = 3) => {
  return withTokenRefresh(() =>
    ajax({
      url: url,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getHeaders(version),
      },
      body,
      async: true,
      responseType: responseType,
      crossDomain: true,
    })
  );
};

export const putJson = (url, body = {}, responseType = "text") => {
  // Headers are built inside the request so a retry after a token refresh
  // sends the new token. (Guest invites authenticate with body.refreshToken.)
  const updatedHeaders = () => (body?.refreshToken ? getHeaders(APP_VERSION, body?.refreshToken) : getHeaders());
  return withTokenRefresh(() =>
    ajax({
      url: url,
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...updatedHeaders(),
      },
      body,
      responseType: responseType,
      crossDomain: true,
    })
  );
};

export const deleteJson = (url, body = {}, responseType = "text") => {
  return withTokenRefresh(() =>
    ajax({
      url: url,
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...getHeaders(),
      },
      body,
      responseType: responseType,
      crossDomain: true,
    })
  );
};

export const postMultipart = (url, body = {}, responseType = "text") => {
  return withTokenRefresh(() =>
    ajax({
      url: url,
      method: "POST",
      headers: {
        // "Content-Type": "multipart/form-data",
        // "type": "formData",
        ...getHeaders(),
      },
      body,
      responseType: responseType,
      crossDomain: true,
    })
  );
};

export const get3Version = (url, headers = {}) => {
  return withTokenRefresh(() =>
    ajax({
      url: url,
      method: "GET",
      responseType: "text",
      headers: {
        ...getHeaders(3),
        ...headers,
      },
      crossDomain: true,
    })
  ).pipe(mergeMap(({ response }) => of(response)));
};

export const downloadFirmware = (url, headers = {}) => {
  return withTokenRefresh(() =>
    ajax({
      url: url,
      method: "GET",
      responseType: "arraybuffer",
      headers: {
        ...getHeaders(3),
        ...headers,
      },
      crossDomain: true,
    })
  );
};

export const postRegisterForm = (url, body = {}, responseType = "text") => {
  return ajax({
    url: url,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-APP-TYPE": "DEVICE",
      "X-TENANT-DOMAIN": TENANT_DOMAIN,
      "X-API-VERSION": 3,
    },
    body,
    async: true,
    responseType: responseType,
    crossDomain: true,
  }).pipe(catchError(unathorized));
};

export const refreshToken = (url) => {
  return ajax({
    url: url,
    method: "POST",
    headers: {
      "Content-Type": "text/plain",
      "X-APP-TYPE": "EUWP",
      "X-TENANT-DOMAIN": TENANT_DOMAIN,
      "X-API-VERSION": 3,
    },
    body: "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzUxMiJ9.eyJlbWFpbEFkZHJlc3MiOiJhZTZkMDFjNC1kOGY5LTRhYzctYTc3Mi1mNmRkYTIyNmU0ZTZAZ2FsZW5jbG91ZC5jb20iLCJyb2xlIjoiVGVuYW50VXNlciIsImFwcFR5cGUiOiJERVZJQ0UiLCJpc3MiOiJHYWxlbkNsb3VkIiwidGVuYW50SWQiOiJBVFMtREVWIiwicHJpbmNpcGFsSWQiOiJhZTZkMDFjNC1kOGY5LTRhYzctYTc3Mi1mNmRkYTIyNmU0ZTYiLCJ0b2tlblR5cGUiOiJNYXN0ZXJUb2tlbiIsImV4cCI6MTg2MTg5NzUzMH0.kMGiKSzkiw76ajEbUvszcNcU4omVc68ywy9QsX6b7AGmoNk9sz7EFpODDXrK_2M0e3Exu0C4dbJ-8Nko_a_Kaw",
    async: true,
    responseType: "text",
    crossDomain: true,
  }).pipe(catchError(unathorized));
};

export const fetchRequest = async (method = "POST", endpoint,  body = {}, fullResponse = false, extraHeaders = {}) => {
  try {
    // Built per attempt so a retry after a token refresh sends the new token.
    const requestOptions = () => ({
        method: method,
        headers: {
          'Content-Type': 'application/json',
          ...getHeaders(),
          ...extraHeaders
        },
        body: JSON.stringify(body)
    });

    const response = await fetchWithTokenRefresh(() => fetch(endpoint, requestOptions()));
    
    if (response.status === 401) {
      unathorized({ status: 401 });
      return {response: response, success: false};
    }
    
    if (!response.ok) {
      // throw new Error('Network response was not ok');
      return {response: response, success: false};
    }
    
    if (fullResponse) {
      return {response: response, success: true};
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching params data:', error);
    return null;
  }
};

export const fetchGetRequest = async (method = "GET", endpoint, fullResponse = false) => {
  try {
    const requestOptions = () => ({
        method: method,
        headers: {
          'Content-Type': 'application/json',
          ...getHeaders()
        }
    });

    const response = await fetchWithTokenRefresh(() => fetch(endpoint, requestOptions()));
    
    if (response.status === 401) {
      unathorized({ status: 401 });
      return {response: response, success: false};
    }
    
    if (!response.ok) {
      // throw new Error('Network response was not ok');
      return {response: response, success: false};
    }
    
    if (fullResponse) {
      return {response: response, success: true};
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching params data:', error);
    return null;
  }
};

// Active device list, cached in memory + IndexedDB for DEVICE_CACHE_TTL_MS so the
// shared filter box does not refetch it on every page load or navigation.
let deviceMemoryCache = null;
let deviceInFlight = null;
onApiCacheClear(() => {
  deviceMemoryCache = null;
  deviceInFlight = null;
});

const getDeviceCacheKey = () => {
  const user = localStorage.getItem("user-details") && JSON.parse(localStorage.getItem("user-details"));
  return `devices:${getCurrentApiBaseUrl()}:${user?.userId || ""}:v${DEVICE_CACHE_SCHEMA_VERSION}`;
};

export const getActiveDevices = async () => {
  const key = getDeviceCacheKey();
  if (deviceMemoryCache?.key === key && Date.now() - deviceMemoryCache.storedAt < DEVICE_CACHE_TTL_MS) {
    return deviceMemoryCache.value;
  }
  if (deviceInFlight?.key === key) return deviceInFlight.promise;

  const promise = (async () => {
    const cached = await getCached(key, DEVICE_CACHE_TTL_MS);
    if (cached) {
      deviceMemoryCache = { key, ...cached };
      return cached.value;
    }
    const value = await fetchGetRequest("GET", `${getCurrentApiBaseUrl()}/user/device?status=Active`);
    // Only successful payloads are cached; errors come back as { success: false } or null.
    if (!Array.isArray(value?.content)) return value;
    deviceMemoryCache = { key, value, storedAt: Date.now() };
    await setCached(key, value);
    return value;
  })();

  deviceInFlight = { key, promise };
  try {
    return await promise;
  } finally {
    if (deviceInFlight?.promise === promise) deviceInFlight = null;
  }
};
